import Bugsnag from '@bugsnag/js'

export type Units = 'cm' | 'in'

export interface IChoice {
  showChooser: boolean
  defaultTo: string
  reason?: string
}

export interface IRegion {
  countryCode: string
  countryName: string
  languageCode: string
  currencyCodes: Array<{ code: string }>
  units: string
  path: string
  flagImage: string
  tax: 'inc_vat' | 'exc_vat'
  cartPath: string
  hreflang: string
}

export interface IRegionWithId extends Omit<IRegion, 'currencyCodes'> {
  id: string
  currencyCode: string
}

const regionMap = {
  EU: [
    'BE',
    'BG',
    'CY',
    'CZ',
    'DK',
    'EE',
    'EL',
    'ES',
    'FI',
    'FR',
    'HR',
    'HU',
    'IE',
    'IS',
    'IT',
    'LI',
    'LT',
    'LU',
    'LV',
    'MT',
    'NL',
    'NO',
    'PT',
    'RO',
    'SE',
    'SI',
    'SK',
  ],
  US: ['CA', 'MX', 'PR'],
}

export const withSlash = (url: string) => (url.endsWith('/') ? url : `${url}/`)
const findMasterRegion = (cc: string) => {
  const found = Object.entries(regionMap).find(([_key, codes]) =>
    codes.includes(cc)
  )

  if (found) {
    return found[0]
  }

  return null
}

export function choose(
  regions: IRegion[],
  url: string,
  maybeGeoCountry: string | null,
  acceptLang: string | null
): IChoice {
  if (process.env.NODE_ENV === 'development') {
    console.log('Choice (Choose)', regions, url, maybeGeoCountry, acceptLang)
  }

  const urlSlash = withSlash(url)
  const exactPathMatches = regions.filter((r) => r.path === urlSlash)
  const prefixMatches = regions.filter((r) => urlSlash.startsWith(r.path))
  const masterRegionCC = findMasterRegion(maybeGeoCountry)

  // Home Page (/)
  //   if / AND GB then showChooser: false, defaultTo: GB
  //   if / AND US then showChooser: true, defaultTo: US
  //   if / AND in EU then showChooser: true, defaultTo: EU
  //   if / AND not in EU then showChooser: true, defaultTo: null
  if (urlSlash === '/') {
    // Exact Match
    const exactGeoMatch = exactPathMatches.find(
      (r) => r.countryCode === maybeGeoCountry
    )
    if (exactGeoMatch) {
      return {
        showChooser: false,
        defaultTo: exactGeoMatch.countryCode,
        reason: `homepage-geo`,
      }
    }

    // Match by master region
    const masterRegion = exactPathMatches.find(
      (r) => r.countryCode === masterRegionCC
    )
    if (masterRegion) {
      return {
        showChooser: true,
        defaultTo: masterRegionCC,
        reason: `homepage-geo-region`,
      }
    }

    // Match by country code
    const ccRegion = regions.find((r) => r.countryCode === maybeGeoCountry)
    if (ccRegion) {
      return {
        showChooser: true,
        defaultTo: ccRegion.countryCode,
        reason: 'homepage-country-is-a-region',
      }
    }

    // Match by master region
    const ccRegion2 = regions.find((r) => r.countryCode === masterRegionCC)
    if (ccRegion2) {
      return {
        showChooser: true,
        defaultTo: ccRegion2.countryCode,
        reason: 'homepage-master-geo-region',
      }
    }

    return {
      showChooser: true,
      defaultTo: regions[0].countryCode,
      reason: 'homepage-fallback',
    }
  }

  // Landed at /de but are in the UK
  const regionsByPath = prefixMatches.sort(
    (a, b) => b.path.length - a.path.length
  )
  if (
    regionsByPath.length > 0 &&
    regionsByPath[0].countryCode !== maybeGeoCountry
  ) {
    const ccRegion = regions.find((r) => r.countryCode === maybeGeoCountry)

    if (ccRegion) {
      return {
        showChooser: true,
        defaultTo: ccRegion.countryCode,
        reason: 'prefix-country-is-a-region',
      }
    }

    // Match by master region instead
    const masterRegion = regions.find((r) => r.countryCode === masterRegionCC)
    if (masterRegion) {
      return {
        showChooser: true,
        defaultTo: masterRegionCC,
        reason: `master-geo-region`,
      }
    }

    return {
      showChooser: true,
      defaultTo: regions[0].countryCode,
      reason: 'prefix-country-fallback',
    }
  }

  const matchers = [
    { reasonPrefix: 'exact-path', matcher: exactPathMatches },
    { reasonPrefix: 'prefix-path', matcher: prefixMatches },
  ]

  const candidates: IChoice[] = matchers.map(({ reasonPrefix, matcher }) => {
    // Exact Geo + Path Match
    // const exactGeoPathMatch = matcher.find((r) => r.countryCode === maybeGeoCountry)
    // if (exactGeoPathMatch) {
    //   return {
    //     showChooser: false,
    //     defaultTo: exactGeoPathMatch.countryCode,
    //     reason: `${reasonPrefix}-geo+pash`,
    //   }
    // }

    // Exact Geo Match
    const exactGeoMatch = matcher.find((r) => r.countryCode === maybeGeoCountry)
    if (exactGeoMatch) {
      return {
        showChooser: false,
        defaultTo: exactGeoMatch.countryCode,
        reason: `${reasonPrefix}-geo`,
      }
    }

    // Match by master region
    const masterRegion = matcher.find((r) => r.countryCode === masterRegionCC)
    if (masterRegion) {
      return {
        showChooser: true,
        defaultTo: masterRegionCC,
        reason: `${reasonPrefix}-geo-region`,
      }
    }
  })

  if (candidates.length > 0) {
    const matched = candidates.find((res) => res)

    if (matched) {
      return matched
    }
  }

  if (regions.length > 0) {
    return { showChooser: true, defaultTo: regions[0].countryCode }
  }

  return {
    showChooser: true,
    defaultTo: null,
  }
}

export const getBrowserLanguages = () => {
  try {
    return navigator.languages.join(';')
  } catch (err) {
    Bugsnag.notify(err)
    return 'en'
  }
}

export const parseUnits = (units?: string) => {
  if (!units) {
    return 'cm'
  }

  if (units === 'imperial') {
    return 'in'
  }

  return 'cm'
}

export const unparseUnits = (units: 'cm' | 'in') => {
  if (units === 'cm') {
    return 'metric'
  }

  return 'imperial'
}

export const setUnits = (v: Units) => localStorage.setItem('si_units', v)
export const setCurrencyCode = (v: string) =>
  localStorage.setItem('si_currencyCode', v)
export const setShipToCountryCode = (v: string) => {
  if (v) {
    localStorage.setItem('si_shipToCountryCode', v)
  }
}
export const setTax = (v: string) => localStorage.setItem('si_tax', v)
export const setCartPath = (v: string) => localStorage.setItem('si_cartPath', v)
export const setPath = (v: string) => localStorage.setItem('si_path', v)
export const setRegion = (region: IRegion) =>
  localStorage.setItem('si_region', JSON.stringify(region))
export const setRegionChooserDismissed = (dismissed: boolean) =>
  localStorage.setItem('si_regionChooserDismissed', JSON.stringify(dismissed))

export const setFromRegion = (
  r: IRegion,
  units: Units,
  currencyCode: string
) => {
  setUnits(units)
  setCurrencyCode(currencyCode)
}
